// components/users/UserTable.jsx

import { Eye, Edit2, Trash2, ShieldOff, Shield, CheckCircle, ChevronUp, ChevronDown, UserCheck } from 'lucide-react';
import { C } from '../../hooks/useUsers.js';
import { ROLE_COLORS, ROLE_LABELS } from '../../models/User.js';

export function UserTable({ users, sort, onSort, selected, onToggle, onToggleAll, onView, onEdit, onDelete, onBlock, onVerify }) {
  const SortIcon = ({ field }) => {
    if (sort.field !== field) return <ChevronUp size={12} style={{ opacity:.3 }} />;
    return sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const TH = ({ field, children }) => (
    <th style={C.th} onClick={() => field && onSort(field)}>
      <span style={{ display:'flex', alignItems:'center', gap:'4px' }}>
        {children}{field && <SortIcon field={field} />}
      </span>
    </th>
  );

  return (
    <table style={C.table}>
      <thead>
        <tr>
          <th style={{ ...C.th, width:'40px' }}>
            <input type="checkbox" checked={selected.length === users.length && users.length > 0}
              onChange={onToggleAll} style={{ cursor:'pointer' }} />
          </th>
          <TH field="customId">ID</TH>
          <TH field="firstName">User</TH>
          <TH>Contact</TH>
          <TH field="role">Role</TH>
          <TH field="isActive">Status</TH>
          <TH field="commissionRate">Commission</TH>
          <TH field="walletBalance">Wallet</TH>
          <TH field="createdAt">Joined</TH>
          <TH field="lastActive">Last Active</TH>
          <th style={C.th}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {users.map(u => (
          <tr key={u.id} style={{ backgroundColor: selected.includes(u.id) ? '#eff6ff' : 'transparent' }}>
            <td style={C.td}>
              <input type="checkbox" checked={selected.includes(u.id)} onChange={() => onToggle(u.id)} style={{ cursor:'pointer' }} />
            </td>
            <td style={C.td}>
              <span style={{ fontSize:'11px', fontFamily:'monospace', color:'#64748b', fontWeight:'600' }}>{u.customId}</span>
            </td>
            <td style={C.td}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }} onClick={() => onView(u)}>
                <div style={{ width:'34px', height:'34px', borderRadius:'50%', backgroundColor:`${ROLE_COLORS[u.role]}20`, border:`2px solid ${ROLE_COLORS[u.role]}40`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:'700', color:ROLE_COLORS[u.role], flexShrink:0 }}>
                  {u.initials}
                </div>
                <div>
                  <div style={{ fontWeight:'600', fontSize:'13px' }}>{u.fullName}</div>
                  <div style={{ fontSize:'11px', color:'#64748b' }}>{u.address?.city || '—'}</div>
                </div>
              </div>
            </td>
            <td style={C.td}>
              <div style={{ fontSize:'12px' }}>{u.email}</div>
              <div style={{ fontSize:'11px', color:'#64748b' }}>{u.phone}</div>
            </td>
            <td style={C.td}>
              <span style={{ ...C.badge, backgroundColor:`${ROLE_COLORS[u.role]}15`, color:ROLE_COLORS[u.role] }}>
                {ROLE_LABELS[u.role] || u.role}
              </span>
            </td>
            <td style={C.td}>
              <span style={{ ...C.badge, backgroundColor:`${u.getStatusColor()}18`, color:u.getStatusColor() }}>
                {u.getStatusLabel()}
              </span>
            </td>
            <td style={C.td}>
              <span style={{ fontWeight:'600', color:'#3b82f6' }}>{u.commissionRate ?? 0}%</span>
            </td>
            <td style={C.td}>
              <span style={{ fontSize:'13px', fontWeight:'500' }}>₹{(u.walletBalance ?? 0).toLocaleString('en-IN')}</span>
            </td>
            <td style={C.td}>
              <span style={{ fontSize:'12px', color:'#64748b' }}>{u.joinDate ? new Date(u.joinDate).toLocaleDateString('en-IN') : '—'}</span>
            </td>
            <td style={C.td}>
              <span style={{ fontSize:'12px', color:'#64748b' }}>{u.getLastActiveLabel ? u.getLastActiveLabel() : '—'}</span>
            </td>
            <td style={C.td}>
              <div style={{ display:'flex', gap:'2px' }}>
                <button style={C.actBtn} title="View"   onClick={() => onView(u)}><Eye size={16} color="#64748b" /></button>
                <button style={C.actBtn} title="Edit"   onClick={() => onEdit(u)}><Edit2 size={16} color="#3b82f6" /></button>
                {!u.isVerified && <button style={C.actBtn} title="Verify" onClick={() => onVerify(u)}><UserCheck size={16} color="#10b981" /></button>}
                <button style={C.actBtn} title={u.isBlocked ? 'Unblock' : 'Block'} onClick={() => onBlock(u)}>
                  {u.isBlocked ? <Shield size={16} color="#10b981" /> : <ShieldOff size={16} color="#f59e0b" />}
                </button>
                <button style={C.actBtn} title="Delete" onClick={() => onDelete(u)}><Trash2 size={16} color="#ef4444" /></button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Pagination ─────────────────────────────────────────────────────────────
export function Pagination({ page, totalPages, total, limit, onPage }) {
  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);
  return (
    <div style={C.pagi}>
      <span style={{ fontSize:'13px', color:'#64748b' }}>Showing {from}–{to} of {total}</span>
      <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
        <button style={{ ...C.btn, ...C.btnSec, padding:'7px 14px', opacity: page===1?.5:1 }}
          onClick={() => onPage(p => Math.max(1,p-1))} disabled={page===1}>Prev</button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let p = i + 1;
          if (totalPages > 5) {
            if (page <= 3) p = i + 1;
            else if (page >= totalPages - 2) p = totalPages - 4 + i;
            else p = page - 2 + i;
          }
          return (
            <button key={p} style={{ ...C.btn, padding:'7px 12px', backgroundColor: p===page ? '#3b82f6' : '#fff', color: p===page ? '#fff' : '#1e293b', border:'1px solid #e2e8f0' }}
              onClick={() => onPage(p)}>{p}</button>
          );
        })}
        <button style={{ ...C.btn, ...C.btnSec, padding:'7px 14px', opacity: page===totalPages?.5:1 }}
          onClick={() => onPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}>Next</button>
      </div>
    </div>
  );
}

// ── Filter Bar ─────────────────────────────────────────────────────────────
export function FilterBar({ filters, onFilter, extra }) {
  return (
    <div style={C.filterBar}>
      <div style={C.searchWrap}>
        <span style={{ ...C.searchIco, fontSize:'16px' }}>⌕</span>
        <input style={C.searchIn} placeholder="Search name, email, phone, ID…"
          value={filters.search || ''} onChange={e => onFilter('search', e.target.value)} />
      </div>
      <select style={C.select} value={filters.status || 'All'} onChange={e => onFilter('status', e.target.value)}>
        <option value="All">All Status</option>
        <option value="Active">Active</option>
        <option value="Inactive">Inactive</option>
        <option value="Blocked">Blocked</option>
        <option value="Unverified">Unverified</option>
      </select>
      {extra}
    </div>
  );
}

// ── User Form Modal ────────────────────────────────────────────────────────
export function UserFormModal({ show, title, formData, onChange, onSubmit, onClose, submitting, errors, roleOptions }) {
  if (!show) return null;

  // ✅ FIXED: wrap onSubmit so the modal auto-closes right after a
  // successful save. onSubmit (from parent) must return a result object
  // like { success: true/false } — see the parent example below.
  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await onSubmit(e);
    if (result?.success !== false) onClose();
  };

  return (
    <div style={C.overlay} onClick={onClose}>
      <div style={C.modal} onClick={e => e.stopPropagation()}>
        <div style={C.mHead}>
          <h2 style={C.mTitle}>{title}</h2>
          <button style={C.actBtn} onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={C.mBody}>
            {errors?.length > 0 && <div style={C.errBox}>{errors.map((e,i) => <div key={i}>• {e}</div>)}</div>}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
              <div style={C.formGroup}><label style={C.label}>First Name *</label><input style={C.input} name="firstName" value={formData.firstName||''} onChange={onChange} required /></div>
              <div style={C.formGroup}><label style={C.label}>Last Name *</label><input style={C.input} name="lastName" value={formData.lastName||''} onChange={onChange} required /></div>
              <div style={C.formGroup}><label style={C.label}>Email *</label><input style={C.input} type="email" name="email" value={formData.email||''} onChange={onChange} required /></div>
              <div style={C.formGroup}><label style={C.label}>Phone *</label><input style={C.input} name="phone" value={formData.phone||''} onChange={onChange} required /></div>
              {!formData.id && <div style={C.formGroup}><label style={C.label}>Password *</label><input style={C.input} type="password" name="password" value={formData.password||''} onChange={onChange} /></div>}
              <div style={C.formGroup}>
                <label style={C.label}>Role *</label>
                <select style={C.input} name="role" value={formData.role||''} onChange={onChange}>
                  {(roleOptions || ['admin','super_distributor','distributor','subdistributor','retailer','client']).map(r => (
                    <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                  ))}
                </select>
              </div>
              <div style={C.formGroup}><label style={C.label}>Commission Rate (%)</label><input style={C.input} type="number" name="commissionRate" value={formData.commissionRate ?? 0} onChange={onChange} min="0" max="100" /></div>
              <div style={C.formGroup}><label style={C.label}>City</label><input style={C.input} name="city" value={formData.address?.city||''} onChange={e => onChange({ target:{ name:'address', value:{ ...formData.address, city:e.target.value } } })} /></div>
            </div>
          </div>
          <div style={C.mFoot}>
            <button type="button" style={{ ...C.btn, ...C.btnSec }} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" style={{ ...C.btn, ...C.btnPrim }} disabled={submitting}>{submitting ? 'Saving…' : (formData.id ? 'Update' : 'Create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── View Modal ─────────────────────────────────────────────────────────────
export function UserViewModal({ show, user, onClose, onEdit, onBlock, onVerify }) {
  if (!show || !user) return null;

  // ✅ FIXED: Verify — await the async action, THEN close the modal
  // (closing before the API call resolves is why it looked like nothing happened)
  const handleVerify = async () => {
    const result = await onVerify(user);
    if (result?.success !== false) onClose();
  };

  // ✅ FIXED: Block/Unblock — same pattern, close only after the action settles
  const handleBlock = async () => {
    const result = await onBlock(user);
    if (result?.success !== false) onClose();
  };

  // ✅ FIXED: Edit — close THIS view modal first, then let parent open the edit form modal
  const handleEdit = () => {
    onClose();
    onEdit(user);
  };

  const Field = ({ label, value }) => (
    <div>
      <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'3px', textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
      <div style={{ fontSize:'14px', fontWeight:'500', color:'#0f172a' }}>{value || '—'}</div>
    </div>
  );
  return (
    <div style={C.overlay} onClick={onClose}>
      <div style={{ ...C.modal, maxWidth:'560px' }} onClick={e => e.stopPropagation()}>
        <div style={C.mHead}>
          <h2 style={C.mTitle}>User Profile</h2>
          <button style={C.actBtn} onClick={onClose}>✕</button>
        </div>
        <div style={C.mBody}>
          {/* Avatar & name */}
          <div style={{ display:'flex', alignItems:'center', gap:'16px', marginBottom:'24px', padding:'16px', backgroundColor:'#f8fafc', borderRadius:'10px' }}>
            <div style={{ width:'56px', height:'56px', borderRadius:'50%', backgroundColor:`${ROLE_COLORS[user.role]}20`, border:`3px solid ${ROLE_COLORS[user.role]}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px', fontWeight:'700', color:ROLE_COLORS[user.role] }}>
              {user.initials}
            </div>
            <div>
              <div style={{ fontSize:'18px', fontWeight:'700', color:'#0f172a' }}>{user.fullName}</div>
              <div style={{ fontSize:'12px', color:'#64748b' }}>{user.customId}</div>
              <span style={{ ...C.badge, backgroundColor:`${user.getStatusColor()}18`, color:user.getStatusColor(), marginTop:'4px' }}>{user.getStatusLabel()}</span>
            </div>
            <div style={{ marginLeft:'auto' }}>
              <span style={{ ...C.badge, backgroundColor:`${ROLE_COLORS[user.role]}15`, color:ROLE_COLORS[user.role] }}>{ROLE_LABELS[user.role]}</span>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' }}>
            <Field label="Email"         value={user.email} />
            <Field label="Phone"         value={user.phone} />
            <Field label="City"          value={user.address?.city} />
            <Field label="Commission"    value={`${user.commissionRate ?? 0}%`} />
            <Field label="Wallet Balance" value={`₹${(user.walletBalance ?? 0).toLocaleString('en-IN')}`} />
            <Field label="Total Earnings" value={`₹${(user.totalEarnings ?? 0).toLocaleString('en-IN')}`} />
            <Field label="Total Orders"  value={user.totalOrders ?? 0} />
            <Field label="Days Active"   value={`${user.getDaysSinceJoining ? user.getDaysSinceJoining() : 0} days`} />
            <Field label="Last Active"   value={user.getLastActiveLabel ? user.getLastActiveLabel() : '—'} />
            <Field label="Verified"      value={user.isVerified ? 'Yes ✓' : 'No'} />
          </div>
        </div>
        <div style={C.mFoot}>
          {!user.isVerified && <button style={{ ...C.btn, ...C.btnGreen }} onClick={handleVerify}><CheckCircle size={15} /> Verify</button>}
          <button style={{ ...C.btn, ...C.btnSec }} onClick={handleBlock}>{user.isBlocked ? 'Unblock' : 'Block'}</button>
          <button style={{ ...C.btn, ...C.btnPrim }} onClick={handleEdit}><Edit2 size={15} /> Edit</button>
        </div>
      </div>
    </div>
  );
}